import { useState, useEffect } from 'react';
import { Image } from 'antd';
import { docmanService } from '@/services/docman.service';

interface S3ImageProps {
  src?: string;
  documentId: string;
  alt?: string;
  width?: number | string;
  height?: number | string;
  style?: React.CSSProperties;
  className?: string;
  preview?: boolean;
  onError?: () => void;
}

const ImageSkeleton = ({ width, height, style }: { width: number | string; height: number | string; style?: React.CSSProperties }) => {
  return (
    <div
      style={{
        width,
        height,
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: '#f0f0f0',
        borderRadius: 4,
        ...style
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.5), transparent)',
          animation: 'wave 1.6s linear infinite',
          transform: 'translateX(-100%)',
        }}
      />
      <style>
        {`
          @keyframes wave {
            0% {
              transform: translateX(-100%);
            }
            100% {
              transform: translateX(100%);
            }
          }
        `}
      </style>
    </div>
  );
};

/**
 * Componente que exibe imagens do S3 e renova automaticamente URLs expiradas
 */
export function S3Image({
  src,
  documentId,
  alt,
  width = 120,
  height = 120,
  style,
  className,
  preview = true,
  onError
}: S3ImageProps) {
  const [imageUrl, setImageUrl] = useState<string | null>(src || null);
  const [base64Url, setBase64Url] = useState<string | null>(null);
  const [loading, setLoading] = useState(!src);
  const [imageLoading, setImageLoading] = useState(!!src);
  const [error, setError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  const loadImage = async (retryDelay = 0) => {
    try {
      setLoading(true);
      setImageLoading(true);
      setError(false);

      if (retryDelay > 0) {
        await new Promise(resolve => setTimeout(resolve, retryDelay));
      }

      if (retryDelay > 0) {
        await new Promise(resolve => setTimeout(resolve, retryDelay));
      }

      const docId = documentId.startsWith('docman/')
        ? documentId.replace('docman/', '')
        : documentId;

      const url = await docmanService.getImageUrl(docId);
      setImageUrl(url);
      setRetryCount(0);
    } catch (err) {
      if (retryCount < 10 && err instanceof Error && err.message.includes('404')) {
        const nextRetryCount = retryCount + 1;
        const delay = Math.min(nextRetryCount * 1000, 5000);
        setRetryCount(nextRetryCount);
        await loadImage(delay);
        return;
      }

      setError(true);
      onError?.();
    } finally {
      setLoading(false);
    }
  };

  const convertToBase64 = async (url: string) => {
    try {
      const response = await fetch(url);

      if (!response.ok) {
        return;
      }

      const blob = await response.blob();

      return new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const result = reader.result as string;
          resolve(result);
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch {
      return null;
    }
  };

  useEffect(() => {
    if (!src && documentId) {
      loadImage();
    }
  }, [documentId]);

  useEffect(() => {
    if (imageUrl) {
      setImageLoading(true);
    }
  }, [imageUrl]);

  const handleImageError = () => {
    if (retryCount < 10) {
      setRetryCount(prev => prev + 1);
      loadImage();
    } else {
      setError(true);
      onError?.();
    }
  };

  if (loading) {
    return <ImageSkeleton width={width} height={height} {...(style && { style })} />;
  }

  if (error || !imageUrl) {
    return (
      <div style={{
        width,
        height,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f5f5f5',
        color: '#999',
        fontSize: 12,
        ...style
      }}>
        Erro ao carregar
      </div>
    );
  }

  const handleImageLoad = async () => {
    setImageLoading(false);

    if (!base64Url && imageUrl && preview) {
      const base64 = await convertToBase64(imageUrl);
      if (base64) {
        setBase64Url(base64);
      }
    }
  };

  return (
    <div style={{ position: 'relative', display: 'inline-block', width, height }}>
      {imageLoading && (
        <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 1 }}>
          <ImageSkeleton width={width} height={height} {...(style && { style })} />
        </div>
      )}
      <Image
        src={imageUrl}
        alt={alt}
        className={className}
        width={width}
        height={height}
        style={{ objectFit: 'cover', ...style, opacity: imageLoading ? 0 : 1, transition: 'opacity 0.3s' }}
        preview={preview ? {
          src: base64Url || imageUrl,
        } : false}
        onLoad={handleImageLoad}
        onError={handleImageError}
      />
    </div>
  );
}
